import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { useTransition, animated } from "@react-spring/web";
import EmailBody from "./components/body";
import EmailConfiguration from "./components/configuration";
import { useDispatch } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { Line } from "rc-progress";

import { AddAssets } from "../asset/add";
import Panel from "src/pages/asset/components/animation/panel";
import EmailToolbar from "./components/toolbar";
import FileUploadService from "src/utils/file.upload.service";
import {
  fetchContentRecommendations,
  invalidateAssetUpload,
} from "src/redux/actions/assets";
import { FileService } from "src/utils/file.service";

import Templates from "./components/templates";
import Widgets from "./components/widgets";
import MessageTemplate from "src/templates/DefaultComposeMessage.handlebars.js";
import DealRoomMessageTemplate from "src/templates/DefaultDealRoomComposeMessage.handlebars.js"
import { getIntegrationForCategory } from "src/legacy/redux/actions/integrations.jsx";

import {
  fetchEmailTemplate,
  fetchDefaultEmailTemplate,
} from "src/redux/actions/emailTemplates";
import { useSelector } from "react-redux";

import Toast from "src/components/toast";
import { toast } from "react-toastify";
import Button from "src/components/button";

import _ from "lodash";
import cx from "classnames";
import styles from "./compose.module.scss";
import CollectionWidgetManager from "src/utils/collections.widget.service";
import { EMAIL_CONFIG } from "./constants";
import {
  createConversation,
  invalidateCreateConversation,
} from "src/redux/actions/conversation";
import { useComposeAdoption } from "src/redux/actions/adoption/compose";
import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import { useDealRoomAdoption } from "src/redux/actions/adoption/dealRoom";
import { MailRecipient } from "src/types/conversations";
import WarningMessage from "../warningMessage";
import DealRoomWidgetManager from "src/utils/dealRoom.widget.service";
import { Asset } from "src/types/assets";
import Text from "../text";

const DefaultAssetIcon = "/images/assets/default_asset.jpg";
const DefaultCollectionIcon = "/images/collections/background.jpeg";
const apiUrl = process.env.REACT_APP_API_URL;

interface ComposeViewProps {
  width?: string;
  page?: boolean;
  dialog?: boolean;
  initialContent?: any;
  emailBody?: string;
  onFinish?: () => void;
  onClose: () => void;
  onLayoutChange?: (changed: boolean) => void;
  sourceType?: string;
  recipientType?: string;
  emailSubject?: string;
}
interface AssetEntity {
  id: string;
  name: string;
  entityType: string;
  assets?: Asset[];
}

interface EmailRequest {
  toAddresses: MailRecipient[];
  ccAddresses: string[];
  bccAddresses: string[];
  message: string;
  subject: string;
  assets: string[];
  collection: any;
  canDownload: boolean;
  canReshare: boolean;
  downloadEditable: boolean;
  enabledBulkEmail: boolean;
  isPasswordEnabled: boolean;
  isExpiryEnabled: boolean;
  engage: boolean;
  expiryDate: any;
  nda: any;
  password: any;
  viewerType: any;
  errorMessage: string;
  isRecommended: boolean;
  recipientType: string;
  dealRoom?: string;
  dealRoomShare?: boolean;
}

interface EmailConfig {
  to: string[];
  cc: string[];
  bcc: string[];
  defaultMessage: string;
  subject: string;
  addAsset: {
    option: any | null;
    entities: AssetEntity[];
  };
  template: any;
  widget: any;
  isDistributionList: boolean;
  contacts: string[];
  editorHeight: 500;
}

const ComposeView: React.FC<ComposeViewProps> = ({
  width,
  page,
  dialog,
  initialContent,
  emailBody,
  sourceType,
  onFinish,
  onClose,
  onLayoutChange,
  recipientType,
  emailSubject
}) => {
  const dispatch = useDispatch();
  const {
    trackConversationSent,
    trackAttachDocumentsClicked,
    trackWidgetsClicked,
    trackTemplatesClicked,
    trackSelectedTemplateClicked,
  } = useComposeAdoption();
  const { trackAddAssetScreenView, trackAssetShared } = useAssetAdoption();
  const { trackCollectionShared } = useCollectionsAdoption();
  const { trackDealRoomUsersAdded } = useDealRoomAdoption();
  const { trackRecommenedAssetClicked } = useComposeAdoption();

  const isDealRoom = _.get(initialContent,"[0].entityType") === "deal_room";
  const widgetManagerRef = useRef(new (isDealRoom ? DealRoomWidgetManager : CollectionWidgetManager)());

  const [showAddAssets, setShowAddAssets] = useState(false);  
  const [showTemplates, setShowTemplates] = useState(false);
  const [showWidgets, setShowWidgets] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showRecommendedAssets, setShowRecommendedAssets] = useState(true);

  const [showOverrideContentWaring, setShowOverrideContentWarning] =
    useState(false);

  const [uploading, setUploading] = useState(false);

  const state = useSelector((state: any) => state);
  const emailIntegration = getIntegrationForCategory(state, "email");

  const user = useSelector((state: any) => _.get(state, "users.me", null));

  const loading = useSelector((state: any) =>
    _.get(
      state,
      "legacyRootReducer.settings.emailTemplate.defaultTemplate.loading",
      false,
    ),
  );
  const loadingIntegrations = useSelector((state: any) =>
    _.get(
      state,
      "legacyRootReducer.settings.integrations.activated.loading",
      false,
    ),
  );
  const paperfliteEmailEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.paperfliteEmailServerEnabled", false),
  );
  const { creating, created } = useSelector((state: any) => {
    return {
      creating: _.get(
        state,
        "conversations.conversation.creation.creating",
        false,
      ),
      created: _.get(
        state,
        "conversations.conversation.creation.created",
        false,
      ),
    };
  });
  const defaultTemplate = useSelector((state: any) =>
    _.get(state, "emailTemplate.defaultTemplate.item", null),
  );
  const theme = useSelector((state) => _.get(state, "accounts.item.theme"));

  const getDefaultMessage = () => {
    if (emailBody && emailBody != null) {
      return emailBody;
    }
    if (defaultTemplate) {
      return defaultTemplate.content;
    }
    if(isDealRoom){
      return DealRoomMessageTemplate({
        emailSignature: (user && user.emailSignature) || "<p>Thank you!</p>",
      })
    }

    return MessageTemplate({
      emailSignature: (user && user.emailSignature) || "<p>Thank you!</p>",
    });
  };

  const shareSettings = useSelector((state: any) =>
    _.get(state, "accounts.item.shareSettings", null),
  );

  const defaultShareSettings = (value) => {
    if (shareSettings) {
      return shareSettings[value];
    }
    return true;
  };

  const [emailRequestState, setEmailRequestState] = useState<EmailRequest>({
    toAddresses: [],
    ccAddresses: [],
    bccAddresses: [],
    message: "",
    subject: emailSubject ?? "",
    assets: [],
    collection: null,
    canDownload: defaultShareSettings("download"),
    canReshare: defaultShareSettings("reshare"),
    downloadEditable: defaultShareSettings("downloadEditable"),
    enabledBulkEmail: true,
    isPasswordEnabled: false,
    isExpiryEnabled: false,
    engage: true,
    expiryDate: null,
    nda: null,
    password: null,
    viewerType: null,
    errorMessage: "",
    isRecommended: false,
    recipientType: recipientType || 'EXTERNAL',
  });

  const [emailConfigState, setEmailConfigState] = useState<EmailConfig>({
    addAsset: {
      option: null,
      entities: [],
    },
    template: null,
    widget: null,
    defaultMessage: getDefaultMessage(),
    to: [],
    cc: [],
    bcc: [],
    subject: emailSubject ?? "",
    isDistributionList: false,
    contacts: [],
    editorHeight: 500,
  });

  const template = useSelector((state: any) => {
    return _.get(
      state,
      `emailTemplate.template.${emailConfigState.template?.id}`,
      null,
    );
  });

  const config = useMemo(() => {
    if (dialog) {
      return {
        from: { opacity: 0, right: "-50%" },
        enter: { opacity: 1, right: "0%" },
        leave: { opacity: 0, left: "-50%" },
      };
    }
  }, [dialog]);

  const isSidePanelOpen = showAddAssets || showTemplates || showWidgets;

  const handleAssetRemove = (entity) => {
    const entityType = entity.entityType;

    if (entityType === "asset-upload") {
      const uploader = FileUploadService.getUploader(entity.id);
      if (uploader) {
        uploader.abortUpload();
        dispatch(invalidateAssetUpload(entity.id));
      }
    }
    const filteredEntities = emailConfigState.addAsset.entities.filter(
      (item) => item.id !== entity.id,
    );

    setEmailConfigState({
      ...emailConfigState,
      addAsset: {
        ...emailConfigState.addAsset,
        entities: filteredEntities,
      },
    });

    setEmailRequestState({
      ...emailRequestState,
      assets: filteredEntities.map((entity) => entity.id),
    });
  };

  const handleEntitySelection = (state) => {
    const uploads = (state.entities || []).filter((entity) =>
      _.includes(["asset-upload", "link", "cleverstory"], entity.entityType),
    );

    const isUpload = _.size(uploads) > 0;
    setUploading(isUpload);

    setEmailConfigState({
      ...emailConfigState,
      defaultMessage: emailRequestState.message, // to retain the message content
    });

    const filteredEntities = (state.entities || []).map((entity) => {
      if (entity.entityType === "collection") {
        const filteredAssets = (entity.assets || []).filter(
          (assetObj) => assetObj.asset?.settings?.share !== false,
        );

        return { ...entity, assets: filteredAssets };
      }
      return entity;
    });

    // Prevent unnecessary updates to avoid flickering
    const updatedState = { ...state, entities: filteredEntities };

    if (!_.isEqual(emailConfigState.addAsset, updatedState)) {
      setEmailConfigState({
        ...emailConfigState,
        addAsset: updatedState,
      });
    }

    const entities = state.entities;
    const collection = entities.find(
      (entity) => entity.entityType === "collection",
    );
    const assets = entities.filter((entity) => entity.entityType === "asset");

    if (_.size(assets) > 0) {
      setEmailRequestState({
        ...emailRequestState,
        assets: assets.map((asset) => asset.id),
        collection: null,
      });
    }

    if (collection) {
      setEmailRequestState({
        ...emailRequestState,
        assets: [],
        collection: collection.id,
        viewerType: _.get(collection, "viewerType"),
      });
    }
  };

  const handleAddAsset = (newAsset) => {
    const updatedEntities = [...emailConfigState.addAsset.entities, newAsset];
    const updatedAssets = updatedEntities.map((entity) => entity.id);

    setEmailConfigState({
      ...emailConfigState,
      defaultMessage: emailRequestState.message, // to retain the message content
      addAsset: {
        ...emailConfigState.addAsset,
        entities: updatedEntities,
      },
    });

    setEmailRequestState({
      ...emailRequestState,
      assets: updatedAssets,
      isRecommended: true,
    });
  };

  const handleToggleAdvancedSettings = () => {
    setShowAdvancedSettings(!showAdvancedSettings);
  };

  const handleToggleRecommendedAssets = () => {
    setShowRecommendedAssets(!showRecommendedAssets);
    trackRecommenedAssetClicked();
  };

  const handleClose = (type) => {
    if (type === "asset") {
      setShowAddAssets(false);
    }

    if (type === "template") {
      setShowTemplates(false);
    }

    if (type === "widget") {
      setShowWidgets(false);
    }
  };

  const handleTemplateSelection = (template) => {
    trackSelectedTemplateClicked({ templateName: template.name });
    setEmailConfigState({
      ...emailConfigState,
      template: template,
    });
    dispatch(fetchEmailTemplate({ id: template.id }));
  };

  const handleWidgetSelection = (widgetId) => {
    const widget = widgetManagerRef.current.widget(widgetId);

    // Create a new object reference to trigger a force update of state as the widget object is not changed
    setEmailConfigState((prevState) => ({
      ...prevState,
      widget: { ...widget },
    }));
  };

  const applyWidgetToMessage = useCallback((message: any) => {
    setEmailConfigState((prevState) => ({
      ...prevState,
      defaultMessage: message,
    }));
  }, []);

  const applyTemplateToMessage = (template: any) => {
    const collection = _.get(template, "collection");
    const assets = _.get(template, "assets");
    const templateSubject = _.get(template, "subject");
    const templateMessage = _.get(template, "content");

    let newEmailRequestState: any = {
      subject: templateSubject
    };

    if (assets) {
      newEmailRequestState = {
        ...newEmailRequestState,
        assets: assets.map((asset) => asset.id),
        collection: null,
      };
    } else if (collection) {
      newEmailRequestState = {
        ...newEmailRequestState,
        assets: [],
        collection: collection.id,
        viewerType: _.get(collection, "viewerType"),
      };
    }

    setEmailRequestState((prevState) => ({
      ...prevState,
      ...newEmailRequestState,
      subject:
        newEmailRequestState.subject === undefined
          ? prevState.subject
          : newEmailRequestState.subject,
    }));

    setEmailConfigState((prevState) => {
      let entities = prevState.addAsset.entities;
      const subject =
        templateSubject === undefined ? prevState.subject : templateSubject;

      const templateHasContent = collection || _.size(assets) > 0;

      if (!(initialContent && !emailConfigState.template)) {
        if (collection) {
          const filteredCollectionAssets = _.get(collection, "assets", []).filter(
            (asset) => _.get(asset, "settings.share", true),
          );
          entities = [{ ...collection, assets: filteredCollectionAssets }];
        } else {
          const filteredAssets = (assets || []).filter((asset) =>
            _.get(asset, "settings.share", true),
          );
          entities = [...filteredAssets];
        }
      }

      return {
        ...prevState,
        subject: subject,
        defaultMessage: templateMessage,
        addAsset: {
          ...prevState.addAsset,
          entities: templateHasContent ? entities : prevState.addAsset.entities,
        },
      };
    });
  };

  const onRecipientsChange = (recipients, type) => {
    // set the addresses in the emailConfigState based on the type

    if (type == EMAIL_CONFIG.TO) {
      setEmailRequestState({
        ...emailRequestState,
        toAddresses: recipients.map((token) => {
          if (token.type == "contact") {
            return {
              type: "email",
              value: token.value.email,
              doNotDisturb: token.value.doNotDisturb,
            };
          } else if (token.type == "contact_list") {
            setEmailConfigState({
              ...emailConfigState,
              isDistributionList: true,
              contacts: [...emailConfigState.contacts, token.value],
            });
            return {
              type: token.type,
              value: token.value.id,
            };
          } else if (token.type == "email") {
            return {
              type: "email",
              value: token.value,
            };
          }
        }),
      });
    }

    if (type == EMAIL_CONFIG.CC || type == EMAIL_CONFIG.BCC) {
      const addressType =
        type === EMAIL_CONFIG.CC ? "ccAddresses" : "bccAddresses";
      setEmailRequestState({
        ...emailRequestState,
        [addressType]: recipients.map((token) => {
          if (token.type == "contact") {
            return {
              type: "email",
              value: token.value.email,
            };
          } else if (token.type == "contact_list") {
            return {
              type: token.type,
              value: token.value.id,
            };
          } else if (token.type == "email") {
            return {
              type: "email",
              value: token.value,
            };
          }
        }),
      });
    }
  };

  const onSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailConfigState({
      ...emailConfigState,
      subject: e.target.value,
    });
    setEmailRequestState({
      ...emailRequestState,
      subject: e.target.value,
    });
  };

  const onEditorMessageChange = useCallback((message: any) => {
    setEmailRequestState((prevState) => ({
      ...prevState,
      message,
    }));
  }, []);

  const processMessage = (message) => {
    const template = widgetManagerRef.current.getRawTemplate(message);
    return template;
  };

  const validateRecipients = () => {
    if (
      emailConfigState.to.length > 0 &&
      !validateEmails(emailConfigState.to)
    ) {
      return false;
    }

    if (
      emailConfigState.cc.length > 0 &&
      !validateEmails(emailConfigState.cc)
    ) {
      return false;
    }

    if (
      emailConfigState.bcc.length > 0 &&
      !validateEmails(emailConfigState.bcc)
    ) {
      return false;
    }

    return true;
  };

  const validateDNDRecipients = () => {
    let valid = true;
    if (_.size(emailRequestState.toAddresses) > 0) {
      valid = !emailRequestState.toAddresses.some(
        (recipient) => recipient.type === "email" && recipient.doNotDisturb,
      );
    }
    return valid;
  };

  const validateEmails = (emails) => {
    const regex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return emails.every((token) => {
      if (token.type === "contact") {
        return regex.test(token.value.email);
      } else if (token.type === "contact_list") {
        return true;
      } else if (token.type === "email") {
        return regex.test(token.value);
      }

      return false;
    });
  };

  const templateOverwriteWarning = () => {
    toast(
      <Toast.warning
        heading="Assets will be replaced!"
        message="Selecting a template will replace the existing assets in your email with assets used in the template. Continue?"
        className={styles.warningToast}
        actions={
          <Button
            variant="primary"
            text={"yes"}
            icon="paperflite-tick"
            className={styles.templateConfirmationButton}
            onClick={() => {
              applyTemplateToMessage(emailConfigState.template);
              setShowOverrideContentWarning(false);
              toast.dismiss();
            }}
          />
        }
        closeToast={() => {
          setEmailConfigState({
            ...emailConfigState,
            template: null,
          });
        }}
      />,
      {
        autoClose: false,
        onClose: () => {
          setShowOverrideContentWarning(false);
        },
      },
    );
  };

  const warningView = () => (
    <div className={styles.warning}>
      <i
        className={cx("paperflite-close", styles.closeIcon)}
        onClick={handleClose}
      ></i>
      <div className={styles.inner}>
        <p className={styles.heading}>
          {loading || loadingIntegrations ? (
            <span className={styles.loading}>Loading...</span>
          ) : (
            "You cannot send emails via Paperflite. Please contact your administrator!"
          )}
        </p>
      </div>
    </div>
  );

  const validateEmailRequest = () => {
    let errorMessage = "";

    switch (true) {
      case !validateRecipients():
        errorMessage = "One or more emails are invalid, Please check!";
        break;

      case !validateDNDRecipients():
        errorMessage =
          "Psst! One or more recipients have enabled DND and will not receive your email.";
        break;

      case !emailRequestState.toAddresses ||
        _.size(emailRequestState.toAddresses) === 0:
        errorMessage =
          "An email this cool deserves a recipient. Add an email ID in the 'To' field.";
        break;

      case !emailRequestState.subject || 
        _.size(emailRequestState.subject) === 0 || 
        emailRequestState.subject.trim() === '' ||
        !emailConfigState.subject ||
        _.size(emailConfigState.subject) === 0 ||
        emailConfigState.subject.trim() === '':
        errorMessage =
          "An email this cool deserves a subject. Fill out the 'Subject' field.";
        break;

      case !emailConfigState.addAsset?.entities ||
        _.size(emailConfigState.addAsset.entities) === 0:
        errorMessage =
          "You've got some great assets waiting to be attached to this email first.";
        break;

      case emailRequestState.isPasswordEnabled && !emailRequestState.password:
        errorMessage = "Looks like you haven't added a password.";
        break;

      case emailRequestState.isExpiryEnabled && !emailRequestState.expiryDate:
        errorMessage = "Looks like you haven't set an expiry date and time.";
        break;

      default:
        return false;
    }

    setEmailRequestState({
      ...emailRequestState,
      errorMessage: errorMessage,
    });

    return true;
  };

  const getCollection = (entities) => {
    if (_.size(entities) === 0) {
      return null;
    }

    return entities.find((entity) => entity.entityType === "collection");
  };

  const getAssets = (entities) => {
    if (_.size(entities) === 0) {
      return null;
    }

    const collection = getCollection(entities);
    if (collection) {
      const assets = _.get(collection, "assets") || [];
      if (_.size(assets) > 0) {
        return assets.map((item) => item.asset || item);
      }
    }

    return entities.filter((entity) => entity.entityType === "asset");
  };

  const collection = getCollection(emailConfigState.addAsset.entities);
  const assets = getAssets(emailConfigState.addAsset.entities);

  const processAssets = (assets) => {
    if (!assets || _.size(assets) === 0) return [];

    return assets.map((item) => {
      if (item?.entityType === "asset") {
        const icon = item.icon || { thumbnail: DefaultAssetIcon };

        return {
          type: "asset",
          asset: {
            ...item,
            icon,
            name: item.name.replace(/(<([^>]+)>)/gi, ""),
            viewerType: _.get(collection, "viewerType"),
            isCleverstoryAsset:
              _.get(item, "asset.viewer.type", "") === "cleverstory_viewer",
          },
        };
      }

      if (item?.entityType === "asset-upload") {
        return {
          ...item,
          file: {
            name: _.get(item, "file.name"),
            icon: DefaultAssetIcon,
            isAssetLimitExeceeded: _.get(item, "error.errors[0].code", 0),
          },
        };
      }

      return null;
    });
  };

  const getCollectionIcon = (collection) => {
    const background = _.get(collection, "banner.background");

    if (background && background.type == "image") {
      return background.full;
    } else if (background && background.type == "video") {
      return background.thumbnail;
    }

    return `${getBaseUrl()}${DefaultCollectionIcon}`;
  };

  const getBaseUrl = () => window.location.origin;

  const insertDefaultWidget = (message) => {
    const defaultWidget = widgetManagerRef.current.widget("assets.grid");

    const parameters = {
      contact: {
        url: `${apiUrl}/api/2.0`,
      },
      collection,
      collectionIcon: collection ? getCollectionIcon(collection) : null,
      sections: collection ? collection.sections : null,
      assets: processAssets(assets),
      theme: theme || {},
      emailSignature: user?.emailSignature || "<p>Thank you!</p>",
    };

    const node = defaultWidget.render(null, parameters);
    const widgetHtml = node.outerHTML;

    message = message + "<p>&nbsp;</p>" + widgetHtml;

    return message;
  };

  const handleSend = () => {
    if (validateEmailRequest()) {
      return;
    }

    const entities = emailConfigState.addAsset.entities;
    var isAssetsShared = "No";
    var isCollectionShared = "No";

    var sourceCta = "";
    if (sourceType === "Share Collection") {
      sourceCta = sourceType;
    } else if (sourceType === "Share Asset") {
      sourceCta = sourceType;
    } else {
      sourceCta = "Compose";
    }

    let message = emailRequestState.message;
    const widgetInserted = widgetManagerRef.current.isWidgetInserted(message);

    if (!widgetInserted) {
      message = insertDefaultWidget(message);
    }

    const collection = entities.find(
      (item) => item.entityType === "collection",
    );
    const assets = entities.filter((item) => item.entityType === "asset");

    if (entities && entities.length > 0) {
      const collection = entities.find(
        (item) => item.entityType === "collection",
      );

      if (collection) {
        isCollectionShared = "Yes";
        trackCollectionShared({ name: collection.name, shareMedium: "Email" });
      }
    }
    const collectionName = _.get(collection, "name");

    let assetType: string[] = [];

    if (_.size(assets) > 0 && !collection) {
      isAssetsShared = "Yes";
      assetType = assets.map((item) => {
        const metadata = _.get(item, "metadata", { content_type: "" });
        return FileService.getFileType(metadata);
      });
    }

    const templateSelected = _.get(emailConfigState, "template") ? "Yes" : "No";
    const is_widgetInserted = _.get(emailConfigState, "widget") ? "Yes" : "No";
    const recommendedAsset = _.get(emailRequestState, "isRecommended")
      ? "Yes"
      : "No";

    if (sourceType === "Share Asset") {
      trackAssetShared({ assetType, shareMedium: "Email" });
    }

    trackConversationSent({ sourceCta, collectionName, assetType, templateSelected, is_widgetInserted, isAssetsShared, isCollectionShared, recommendedAsset})
    let conversation = { ...emailRequestState, message: processMessage(message) };
    const isDealRoom = _.get(initialContent,"[0].entityType") === "deal_room";
    if(isDealRoom){
      conversation = {
        ...conversation,
        viewerType: "DEAL_ROOM",
        dealRoom: _.get(initialContent,"[0].id",""),
        dealRoomShare: true,
        recipientType: "EXTERNAL",
        engage: true,
        canReshare: true
      }

      const newDealRoomExternalUsers: string[] = [];
      const { toAddresses, ccAddresses, bccAddresses } = conversation;
      const allAddresses = [...toAddresses, ...ccAddresses, ...bccAddresses];

      allAddresses.forEach(address => {
        if (_.get(address, "type", "") === "email") {
          newDealRoomExternalUsers.push(_.get(address, "value", ""));
        }
      });

      allAddresses.forEach(address => {
        trackDealRoomUsersAdded({
          dealRoomName: _.get(initialContent,"[0].name", ""),
          type: "EXTERNAL",
          userId: _.get(address, "value", "")
        })
      });
    }

    dispatch(createConversation(conversation));
  };

  const dismissErrorMessage = () => {
    setEmailRequestState({
      ...emailRequestState,
      errorMessage: "",
    });
  };

  const shouldDisableSend = () => {
    const assets = _.get(emailConfigState, "addAsset.entities", []);

    const hasError = assets.some(
      (asset) => _.get(asset, "error.errors[0].code") === 21007,
    );

    return hasError || creating || emailRequestState.errorMessage || uploading;
  };

  const handleToggle = (key: string) => {
    if (key === "enableNDA" || key === "enablePassword") {
      return;
    }

    setEmailRequestState({
      ...emailRequestState,
      [key]: !emailRequestState[key],
    });
  };

  const handleSelection = (key: string, value?: any) => {
    const fields = [
      "nda",
      "expiryDate",
      "password",
      "isPasswordEnabled",
      "isExpiryEnabled",
    ];
    if (fields.includes(key)) {
      setEmailRequestState({
        ...emailRequestState,
        [key]: value,
      });
    }
  };

  const uploadView = () => {
    const assets = (emailConfigState.addAsset.entities || [])
      .filter((asset) =>
        ["asset-upload", "link", "cleverstory"].includes(asset.entityType),
      )
      .map((asset) => (
        <Row key={asset.id} className={styles.asset}>
          <Col md={8}>
            <div className={styles.name}>
              <Text text={asset.entityType === "asset-upload"
                  ? _.get(asset, "name", "Untitled")
                  : _.get(asset, "url", "Untitled")} />
            </div>
          </Col>
          <Col md={4}>
            <div className={styles.progress}>
              {asset.entityType === "asset-upload" ? (
                <Line
                  percent={_.get(asset, "progress", 0)}
                  strokeWidth={5}
                  trailWidth={5}
                  strokeColor="#e51058"
                />
              ) : (
                "Creating..."
              )}
            </div>
          </Col>
        </Row>
      ));

    return (
      <div className={styles.uploading}>
        <div className={styles.inner}>
          <p className={styles.heading}>Uploading {assets.length} Asset...</p>
          <div className={styles.assets}>{assets}</div>
        </div>
      </div>
    );
  };

  const handleBulkEmailChange = (enabled) => {
    setEmailRequestState({
      ...emailRequestState,
      enabledBulkEmail: enabled,
    });
  };

  useEffect(() => {
    dispatch(fetchDefaultEmailTemplate());
    dispatch(fetchContentRecommendations([]));

    if (initialContent) {
      const entities =
        _.size(initialContent) > 0 ? initialContent : [initialContent];

      setEmailConfigState({
        ...emailConfigState,
        addAsset: {
          ...emailConfigState.addAsset,
          entities: entities,
        },
      });

      const collection = entities.find(
        (entity) => entity.entityType === "collection",
      );
      const assets = entities.filter((entity) => entity.entityType === "asset");

      setEmailRequestState({
        ...emailRequestState,
        assets: assets.map((asset) => asset.id),
      });

      if (collection) {
        setEmailRequestState({
          ...emailRequestState,
          collection: collection.id,
          viewerType: _.get(collection, "viewerType"),
        });
      }
    }
  }, []);

  useEffect(() => {
    // Layout changes for compose dialog with panel
    if (dialog && onLayoutChange) {
      if (isSidePanelOpen) {
        onLayoutChange(true);
      } else {
        onLayoutChange(false);
      }
    }
  }, [showTemplates, showWidgets, showAddAssets]);

  useEffect(() => {
    if (template && template.fetched) {
      const item = _.get(template, "item");
      const collection = _.get(item, "collection");
      const assets = _.get(item, "assets");

      const templateHasContent = collection || _.size(assets) > 0;
      const hasContent = _.size(emailConfigState.addAsset.entities) > 0;
      setEmailConfigState({
        ...emailConfigState,
        template: item,
      });

      if (hasContent && templateHasContent) {
        setShowOverrideContentWarning(true);
      } else {
        applyTemplateToMessage(item);
      }
    }
  }, [template]);

  useEffect(() => {
    if (!(emailBody || initialContent) && defaultTemplate) {
      applyTemplateToMessage(defaultTemplate);
    }
  }, [defaultTemplate]);

  useEffect(() => {
    if (showOverrideContentWaring) {
      templateOverwriteWarning();
      setShowOverrideContentWarning(false);
    }
  }, [emailRequestState.errorMessage, showOverrideContentWaring]);

  useEffect(() => {
    if (created) {
      onClose();
      dispatch(invalidateCreateConversation());
    }
  }, [created]);

  return (
    <>
    <div className={cx({[styles.blur]: uploading || loadingIntegrations})}>
      <section className={cx(styles.layout, {[styles.blurView]: loadingIntegrations || (!paperfliteEmailEnabled && !emailIntegration)})}>
      <EmailBody 
        uploading={uploading}
        onClose={onClose}
        emailConfigState={emailConfigState}
        onRecipientsChange={onRecipientsChange}
        onSubjectChange={onSubjectChange}
        onEditorMessageChange={onEditorMessageChange}
        applyWidgetToMessage={applyWidgetToMessage}
        onSend={handleSend}
        disabled={shouldDisableSend()}
        emailRequestState ={emailRequestState}
        onBulkEmailChange = {handleBulkEmailChange}
        processAssets = {processAssets}
        getCollectionIcon={getCollectionIcon}
        isDealRoom={isDealRoom}
      />

          <div className={styles.verticalBorder}></div>

      <EmailConfiguration
        className={cx(styles.configuration, {
          [styles.sidePanelOpen]: isSidePanelOpen,
        })}
        onAssetRemove={handleAssetRemove}
        entities={_.get(emailConfigState.addAsset, "entities", [])}
        onToggle={(key : string) => handleToggle(key)}
        onSelection={(key : string , value? :any) => handleSelection(key, value)}
        emailRequestState={emailRequestState}
        onAssetAdd= {handleAddAsset}
        emailConfigState = {emailConfigState}
        isSidePanelOpen ={isSidePanelOpen}
        dialog={!!dialog}
        advancedSettings = {showAdvancedSettings}
        handleAdvancedSettings = {handleToggleAdvancedSettings}
        handleRecommendedAssets = {handleToggleRecommendedAssets}
        showRecommendedAssets = {showRecommendedAssets}
        onClose ={onClose}
        isDealRoom={isDealRoom}
      />
      
      <EmailToolbar
        show={!isSidePanelOpen}
        dialog={dialog}
        handleAddAssets={() => {
          trackAttachDocumentsClicked();
          trackAddAssetScreenView();
          setShowAddAssets(true);
        }}
        handleTemplates={() => {
          trackTemplatesClicked();
          setShowTemplates(true)
        }}
        handleWidgets={() => {
          trackWidgetsClicked();
          setShowWidgets(true)
        }}
        isDealRoom={isDealRoom}
      />
      
      <Panel
        show={showAddAssets}
        config={config}
        className={styles.panelContainer}
      >
        <div className={styles.addassetWrapper}>
        <AddAssets
          className={styles.composeAddAsset}
          width={415}
          state={emailConfigState.addAsset}
          selectableEntityTypes={["asset", "collection"]}
          source={{
            type: "compose",
            entity: null,
          }}
          onStateChange={handleEntitySelection}
          onClose={() => handleClose("asset")}
        />
        </div>
      </Panel>

          <Panel
            show={showTemplates}
            config={config}
            className={styles.panelContainer}
          >
            <Templates
              width={375}
              onCloseTemplate={onClose}
              onClose={() => handleClose("template")}
              selectedTemplate={emailConfigState.template}
              onSelection={handleTemplateSelection}
            />
          </Panel>

      <Panel
        show={showWidgets}
        config={config}
        className={styles.panelContainer}
      >
        <Widgets isDealRoom={isDealRoom} width={375} onClose={() => handleClose("widget")} onSelection={handleWidgetSelection} />
      </Panel>
    </section>
    </div>
    {(loading || loadingIntegrations || (!paperfliteEmailEnabled && !emailIntegration)) && warningView()}
    {uploading && uploadView()}

      {emailRequestState.errorMessage && (
        <div className={styles.confirmationMessage}>
          <WarningMessage
            type="WARNING"
            header="psst!"
            actions={
              <button
                className={styles.closeButton}
                onClick={dismissErrorMessage}
              >
                <i className="fa-regular fa-circle-xmark"></i>
              </button>
            }
            className={styles.message}
            message={emailRequestState.errorMessage}
          />
        </div>
      )}
    </>
  );
};

export default ComposeView;